import {Injectable} from "@angular/core";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {TradeCrudComponent} from "../../../crystal-clear/support/trade-crud/trade-crud.component";
import {Trade} from "../../../model/clearing/trade";
import {resolveFieldTypes, FieldKind} from "../../../utils/object-reflection";
import {buildFormField} from "../../../utils/form-helper";
import {FieldRecord} from "../../../model/api/field-record";
import {SupportTradeDataLoadingService} from "./support-trade-data-loading.service";
import {Account} from "../../../model/clearing/account";
import TYPE = FormFieldConfig.TYPE;
import {TableColumnUtils} from "../../../utils/table-column-utils";
import {TableColumnConfig} from "../../../model/api/table-column-config";

@Injectable({
  providedIn: 'root'
})
export class SupportTradeFormConfigService {

  public static readonly fieldTypes = resolveFieldTypes<Trade>({

    // ------------------------------------------------------------------
    // ENUMS
    // ------------------------------------------------------------------
    side: 'enum',
    state: 'enum',
    callPut: 'enum',
    agressorInd: 'enum',
    openClose: 'enum',
    initialOpenClose: 'enum',

    // ------------------------------------------------------------------
    // NUMBERS
    // ------------------------------------------------------------------
    price: 'number',
    avgPrice: 'number',
    avgPriceResidual: 'number',
    avgPriceResidualGroup: 'number',
    avgPriceResidualAlloc: 'number',
    overrideAvgPrice: 'number',
    avgRndPrice: 'number',
    volume: 'number',
    unmatchedVolume: 'number',
    strike: 'number',
    obLegRank: 'number',

    // ------------------------------------------------------------------
    // BOOLEANS
    // ------------------------------------------------------------------
    giveIn: 'boolean',
    giveUp: 'boolean',
    finalPrice: 'boolean',
    exchangeMatched: 'boolean',
    reversalRequested: 'boolean',
    reversalRejected: 'boolean',

    // ------------------------------------------------------------------
    // DATES (java.sql.Date equivalent)
    // ------------------------------------------------------------------
    executionDate: 'date',
    businessDate: 'date',
    clearingDate: 'date',

    // ------------------------------------------------------------------
    // DATETIMES (Luxon)
    // ------------------------------------------------------------------
    creationDate: 'datetime',
    modificationDate: 'datetime',

    // ------------------------------------------------------------------
    // OBJECTS
    // ------------------------------------------------------------------
    account: 'object',
    clearingInfo: 'object',

    // ------------------------------------------------------------------
    // ARRAYS
    // ------------------------------------------------------------------
    clearings: 'array'

  });


  public loadConfig(component: TradeCrudComponent): FormFieldGroupConfig[] {

    const record = component.newRecord;
    if (!record) {
      return [];
    }

    const autoFields: FormFieldConfig[] =
      (Object.keys(record) as Array<keyof Trade>)
        .filter(field => !this.isExcluded(field))
        .map(field =>
          buildFormField<Trade>(field, SupportTradeFormConfigService.fieldTypes[field])
        );

    autoFields.forEach(field => field.options = SupportTradeFormConfigService.buildOptions(field, component.dataLoadingService));

    const accountFields: FormFieldConfig[] =
      (Object.keys(record.account) as Array<keyof Account>)
        .map(field => buildFormField<Account>(field, SupportTradeFormConfigService.fieldTypes[field]));
    const accountFieldTableConfig: TableColumnConfig[] = (Object.keys(component.newRecord.account ?? {}) as Array<keyof Account>)
      .map(field => {
        return TableColumnUtils.buildColumn<Account>(
          field,
          SupportTradeFormConfigService.fieldTypes[field],
        );
      });

    return [
      {
        group: 'account',
        fields: [
          {
            field: 'account',
            column: 12,
            type: TYPE.SINGLERICHFIELD,
            richFieldConfig: {
              fieldId: 'name',
              fieldName: 'name',
              tableConfigs: accountFieldTableConfig,
              fields: [
                {
                  group: '',
                  fields: accountFields
                }]
            }
          }
        ]
      }
      , {
        group: 'fields',
        fields: autoFields
      }
    ];
  }

  private isExcluded(field: keyof Trade): boolean {
    return [
      'clearings',
      'clearingInfo',
      'account'
    ].includes(field as string);
  }

  // ---------------------------------------------------------------------
  // Trade-specific options
  // ---------------------------------------------------------------------

  public static buildOptions(
    fieldConf: FormFieldConfig,
    dataLoadingService: SupportTradeDataLoadingService
  ): () => FieldRecord<any>[] {

    switch (fieldConf.field) {
      case 'callPut':
        return () => dataLoadingService.callPuts;
      case 'agressorInd':
        return () => dataLoadingService.agressors;
      case 'side':
        return () => dataLoadingService.sides;
      case 'state':
        return () => dataLoadingService.tradeStates;
      case 'openClose':
      case 'initialOpenClose':
        return () => dataLoadingService.openCloses;
      default:
        return () => undefined;
    }
  }
}
